import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../services/apiClient';
import { CertsPanel } from './EmployeeCerts';
import AskCards from './AskCards';
import TimeOffModal from '../pages/TimeOffModal';

const QUESTIONS = [{ key: 'did', label: 'Done today' }, { key: 'going', label: "How it's going" }, { key: 'need', label: 'Need tomorrow' }, { key: 'done', label: 'Done by' }];

// My Information (Ryan, Oct 14 2026) — the phone's "My Cards" grew into a home for things about
// yourself: general information, your cards, your past daily updates, your answers to the office's
// asks. More tiles to come (safety scores…): add one to TILES and a case below.
const TILES = [
  { key: 'general', icon: '👤', label: 'General Information', sub: 'Name, role, phone, truck' },
  { key: 'cards', icon: '🪪', label: 'My Cards', sub: 'License, OSHA, CPR, DOT…' },
  { key: 'updates', icon: '📝', label: 'My Daily Updates', sub: 'What you’ve posted' },
  { key: 'answers', icon: '💬', label: 'My Answers', sub: 'Asks from the office' },
  { key: 'hours', icon: '⏱️', label: 'My Hours', sub: 'This week and last, by day' },
  { key: 'timeoff', icon: '🌴', label: 'My Time Off', sub: 'Coming up, waiting, past' },
  { key: 'safety', icon: '🦺', label: 'My Safety', sub: 'Weekly safety videos' },
];
const when = s => (s ? new Date(s).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const Row = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f4f6', fontSize: 15 }}>
    <span style={{ width: 110, flexShrink: 0, color: '#6b7280', fontSize: 13, fontWeight: 700 }}>{label}</span>
    <span style={{ color: '#111', minWidth: 0 }}>{children || <span style={{ color: '#9ca3af' }}>—</span>}</span>
  </div>
);

function General() {
  const [me, setMe] = useState(null);
  useEffect(() => { apiFetch('/my-info').then(setMe).catch(() => setMe({})); }, []);
  if (!me) return <div style={{ color: '#9ca3af', padding: 10 }}>Loading…</div>;
  return (
    <div>
      <Row label="Name">{me.name}</Row>
      <Row label="Role">{me.role_label}</Row>
      <Row label="Trade">{me.trade}</Row>
      <Row label="Phone">{me.phone}</Row>
      <Row label="Email">{me.email}</Row>
      <Row label="CDL">{me.cdl ? 'Yes' : 'No'}</Row>
      <Row label="Truck / equipment">{me.equipment && me.equipment.length ? me.equipment.map((e, i) => <div key={i}><b>{[e.asset_number, e.name].filter(Boolean).join(' — ')}</b>{e.at ? <span style={{ color: '#6b7280' }}> · {e.at}</span> : null}</div>) : null}</Row>
      <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 12 }}>Something wrong here? Tell the office and they’ll fix it.</div>
    </div>
  );
}

function Updates() {
  const [data, setData] = useState(null);
  useEffect(() => { apiFetch('/my-info/updates?days=60').then(setData).catch(() => setData({ rows: [] })); }, []);
  if (!data) return <div style={{ color: '#9ca3af', padding: 10 }}>Loading…</div>;
  if (!data.rows.length) return <div style={{ color: '#9ca3af', padding: 10 }}>No updates in the last {data.days} days.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 12.5, color: '#6b7280' }}>{data.rows.length} update{data.rows.length === 1 ? '' : 's'} in the last {data.days} days, newest first.</div>
      {data.rows.map(r => (
        <div key={r.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 12px', background: '#fff' }}>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>{when(r.created_at)}{r.project_number || r.job ? ` · ${r.project_number || r.job}` : ''}</div>
          <div style={{ fontSize: 14.5, color: '#111', whiteSpace: 'pre-wrap', marginTop: 4 }}>{r.text}</div>
          {Array.isArray(r.answered) && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
              {QUESTIONS.map(q => <span key={q.key} style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: r.answered.includes(q.key) ? '#dcfce7' : '#f3f4f6', color: r.answered.includes(q.key) ? '#166534' : '#9ca3af' }}>{r.answered.includes(q.key) ? '✓' : '✕'} {q.label}</span>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const dayLabel = d => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const hrs = n => `${(Math.round(n * 100) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })} h`;

// Also on the Time screen (Matthew Thiessen, Oct 19 2026: "we can't see our hours broken down by day anymore").
export function MyHours() { return <Hours />; }
const clockT = iso => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');
// "Wrong job? Fix it" (Ryan, Nov 2 2026): one of your own shifts from the last seven days, put on the
// job you were really on. Only the job changes — the hours stay exactly as they were, and the office
// hears about it. Pick from your jobs or type a number, one tap to confirm, then it is done.
function FixJob({ shift, onDone, onCancel }) {
  const [mine, setMine] = useState([]);
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { apiFetch('/time/clockable-jobs').then(r => setMine(Array.isArray(r) ? r : [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!q.trim()) { setFound([]); return undefined; }
    const t = setTimeout(() => apiFetch(`/time/find-job?q=${encodeURIComponent(q.trim())}`).then(r => setFound(Array.isArray(r) ? r : [])).catch(() => setFound([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  const num = j => (j.project_number ? String(j.project_number).split(' - ')[0].trim() : '');
  const list = (q.trim() ? found : mine).filter(j => j.id !== shift.projectId).slice(0, 12);
  async function go() {
    if (!pick || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await apiFetch('/time/fix-job', { method: 'POST', body: JSON.stringify({ sheetId: shift.id, projectId: pick.id }) });
      onDone(r);
    } catch (e) { setErr((e && (e.error || e.message)) || 'That did not save.'); setBusy(false); }
  }
  const row = j => (
    <button key={j.id} onClick={() => setPick(j)} style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${pick && pick.id === j.id ? '#1e3a5f' : '#e5e7eb'}`, background: pick && pick.id === j.id ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: 14 }}>
      <b style={{ color: '#1e3a5f' }}>{num(j)}</b> <span style={{ color: '#374151' }}>{j.name}</span>
    </button>
  );
  return (
    <div style={{ background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#111' }}>Which job were you really on {dayLabel(shift.date)}?</div>
      <div style={{ fontSize: 12.5, color: '#6b7280' }}>The {hrs(shift.hours)} stay as they are — only the job changes. The office and the PMs get told.</div>
      <input value={q} onChange={e => { setQ(e.target.value); setPick(null); }} placeholder="Type a job number, or pick from your jobs below" inputMode="text"
        style={{ width: '100%', padding: '11px 12px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, boxSizing: 'border-box' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
        {list.length ? list.map(row) : <div style={{ color: '#9ca3af', fontSize: 13, padding: '6px 2px' }}>{q.trim() ? `No active job matches “${q}”.` : 'Loading your jobs…'}</div>}
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 700 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={go} disabled={!pick || busy} style={{ flex: 1, padding: 12, borderRadius: 10, border: 'none', background: pick ? '#15803d' : '#cbd5e1', color: '#fff', fontSize: 14.5, fontWeight: 800, cursor: pick ? 'pointer' : 'default' }}>
          {busy ? 'Saving…' : pick ? `✓ Move it to ${num(pick) || pick.name}` : 'Pick the job'}
        </button>
        <button onClick={onCancel} disabled={busy} style={{ padding: '12px 14px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
      </div>
    </div>
  );
}
function Hours() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [fixing, setFixing] = useState(null);   // the shift being put on another job
  const [done, setDone] = useState('');
  const load = () => apiFetch('/my-info/hours').then(setD).catch(e => { setErr((e && (e.error || e.message)) || 'Could not load.'); setD({ weeks: [] }); });
  useEffect(() => { load(); }, []);
  if (!d) return <div style={{ color: '#9ca3af', padding: 10 }}>Reading QuickBooks Time…</div>;
  if (err) return <div style={{ color: '#b91c1c', padding: 10, fontWeight: 700 }}>{err}</div>;
  const shifts = Array.isArray(d.shifts) ? d.shifts : [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {d.weeks.map(w => (
        <div key={w.label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #eef0f2' }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: '#111' }}>{w.label} <span style={{ fontWeight: 600, color: '#6b7280', fontSize: 12 }}>from {dayLabel(w.start)}</span></span>
            <span style={{ fontSize: 18, fontWeight: 800, color: '#1e3a5f' }}>{hrs(w.total)}</span>
          </div>
          {w.days.map(x => (
            <div key={x.date} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 14 }}>
              <span style={{ width: 92, color: '#374151', fontWeight: 700 }}>{dayLabel(x.date)}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.jobs.join(', ')}{x.on_clock ? ' · on the clock' : ''}</span>
              <span style={{ fontWeight: 800, color: x.hours ? '#111' : '#d1d5db' }}>{x.hours ? hrs(x.hours) : '—'}</span>
              {x.hours > 0 && <span style={{ width: 56, textAlign: 'right', fontSize: 12, fontWeight: 800, color: x.weekend || x.vs8 > 0 ? '#15803d' : x.vs8 < 0 ? '#b45309' : '#6b7280' }}>{x.weekend ? 'weekend' : x.vs8 === 0 ? '8 h' : `${x.vs8 > 0 ? '+' : ''}${x.vs8}`}</span>}
            </div>
          ))}
        </div>
      ))}
      {/* The shifts themselves, newest first — the job on each can be fixed; the hours cannot. */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #eef0f2', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#111' }}>My shifts <span style={{ fontWeight: 600, color: '#6b7280', fontSize: 12 }}>last {d.fixDays || 7} days</span></span>
          <span style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 700 }}>Wrong job? Fix it here</span>
        </div>
        {done && <div style={{ padding: '8px 12px', background: '#dcfce7', color: '#166534', fontSize: 13, fontWeight: 700 }}>{done}</div>}
        {!shifts.length && <div style={{ padding: '14px 12px', color: '#9ca3af', fontSize: 13 }}>No shifts in the last {d.fixDays || 7} days.</div>}
        {shifts.map(x => (
          <div key={x.id} style={{ padding: '9px 12px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
              <span style={{ width: 92, color: '#374151', fontWeight: 700, flexShrink: 0 }}>{dayLabel(x.date)}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 800, color: '#1e3a5f' }}>{x.job || '—'}</span>
                <span style={{ fontSize: 12, color: '#6b7280' }}> · {clockT(x.start)}{x.end ? `–${clockT(x.end)}` : ' · on the clock'}</span>
              </span>
              <span style={{ fontWeight: 800, color: '#111' }}>{x.hours ? hrs(x.hours) : '—'}</span>
            </div>
            {fixing && fixing.id === x.id
              ? <div style={{ marginTop: 8 }}><FixJob shift={x} onCancel={() => setFixing(null)} onDone={r => { setFixing(null); setDone(`✓ ${dayLabel(r.date)} moved from ${r.from} to ${r.to} — hours unchanged. The office has been told.`); setD(null); load(); }} /></div>
              : x.fixable
                ? <button onClick={() => { setDone(''); setFixing(x); }} style={{ marginTop: 4, background: 'none', border: 'none', color: '#b45309', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', padding: 0 }}>Wrong job? Fix it</button>
                : x.why ? <div style={{ marginTop: 2, fontSize: 11.5, color: '#9ca3af' }}>{x.why}</div> : null}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12, color: '#6b7280' }}>From QuickBooks Time. Lunch isn’t counted. You can put a shift on the right job here; the hours themselves are the office’s to change — add a note on the shift from the Time Clock, or tell them.</div>
    </div>
  );
}

const KIND = { vacation: 'Vacation', sick: 'Sick', personal: 'Personal', other: 'Other' };
const ST = { approved: ['Approved', '#dcfce7', '#166534'], pending: ['Waiting', '#fef3c7', '#92400e'], denied: ['Denied', '#fee2e2', '#991b1b'] };
function TimeOff() {
  const [rows, setRows] = useState(null);
  const [asking, setAsking] = useState(false);
  const load = () => apiFetch('/my-time-off').then(r => setRows(Array.isArray(r) ? r : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows) return <div style={{ color: '#9ca3af', padding: 10 }}>Loading…</div>;
  const today = new Date().toLocaleDateString('en-CA');
  const upcoming = rows.filter(r => r.status === 'approved' && String(r.end_date || r.start_date) >= today).sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
  const waiting = rows.filter(r => r.status === 'pending');
  const past = rows.filter(r => !upcoming.includes(r) && !waiting.includes(r)).slice(0, 15);
  const range = r => (r.end_date && r.end_date !== r.start_date ? `${dayLabel(r.start_date)} – ${dayLabel(r.end_date)}` : dayLabel(r.start_date));
  const item = r => { const st = ST[r.status] || [r.status, '#f3f4f6', '#374151']; return (
    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 12px' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: '#111' }}>{range(r)}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{KIND[r.kind] || 'Time off'}{r.reason ? ` · ${r.reason}` : ''}</div>
      </div>
      <span style={{ fontSize: 11.5, fontWeight: 800, padding: '3px 9px', borderRadius: 999, background: st[1], color: st[2] }}>{st[0]}</span>
    </div>); };
  const head = t => <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>{t}</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button onClick={() => setAsking(true)} style={{ padding: 12, borderRadius: 12, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>🌴 Request time off</button>
      {head('Coming up')}{upcoming.length ? upcoming.map(item) : <div style={{ color: '#9ca3af', fontSize: 13 }}>Nothing approved coming up.</div>}
      {waiting.length > 0 && <>{head('Waiting for an answer')}{waiting.map(item)}</>}
      {past.length > 0 && <>{head('Past')}{past.map(item)}</>}
      {asking && <TimeOffModal onClose={() => { setAsking(false); load(); }} />}
    </div>
  );
}

const VSTATE = { on_time: ['Done on time', '#15803d'], late: ['Done late', '#b45309'], open: ['Not done yet', '#1d4ed8'], overdue: ['Overdue', '#b91c1c'] };
function Safety() {
  const [d, setD] = useState(null);
  useEffect(() => { apiFetch('/my-info/safety').then(setD).catch(() => setD({})); }, []);
  if (!d) return <div style={{ color: '#9ca3af', padding: 10 }}>Loading…</div>;
  const m = d.mine;
  if (!m) return <div style={{ color: '#6b7280', padding: 10, fontSize: 14 }}>The weekly safety videos aren’t assigned to you. Anything assigned to you shows on the Safety tile.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, textAlign: 'center' }}>
          <div style={{ fontSize: 30, fontWeight: 800, color: m.score == null ? '#9ca3af' : m.score >= 90 ? '#15803d' : m.score >= 70 ? '#b45309' : '#b91c1c' }}>{m.score == null ? '—' : m.score}</div>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>Your safety score</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, textAlign: 'center' }}>
          <div style={{ fontSize: 30, fontWeight: 800, color: m.caught_up ? '#15803d' : '#b91c1c' }}>{m.caught_up ? '✓' : m.overdue}</div>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>{m.caught_up ? 'All caught up' : `overdue video${m.overdue === 1 ? '' : 's'}`}</div>
        </div>
      </div>
      <div style={{ fontSize: 12.5, color: '#6b7280' }}>{m.on_time} on time · {m.late} late · {m.open} open · {m.overdue} overdue, of {m.assigned} since you started. Score = on time, plus half for late, out of everything due.</div>
      {m.videos.slice(0, 12).map(v => { const st = VSTATE[v.state] || [v.state, '#374151']; return (
        <div key={v.assignment_id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '9px 12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</div>
            <div style={{ fontSize: 11.5, color: '#6b7280' }}>Due {dayLabel(v.due)}{v.completed_at ? ` · done ${dayLabel(String(v.completed_at).slice(0, 10))}` : ''}</div>
          </div>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: st[1], whiteSpace: 'nowrap' }}>{st[0]}</span>
        </div>); })}
      {!m.caught_up && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 700 }}>Watch and sign the overdue ones from the Safety tile.</div>}
    </div>
  );
}

export function MyInfoSheet({ onClose, initial = null }) {
  const [tab, setTab] = useState(initial);
  const t = TILES.find(x => x.key === tab);
  return createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#f9fafb', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 520, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px 12px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #eef0f2', background: '#fff' }}>
          {t && <button onClick={() => setTab(null)} aria-label="Back" style={{ background: 'none', border: 'none', fontSize: 26, color: '#6b7280', cursor: 'pointer', lineHeight: 1, padding: '0 4px 0 0' }}>‹</button>}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#111' }}>{t ? `${t.icon} ${t.label}` : '🙋 My Information'}</div>
            {!t && <div style={{ fontSize: 12, color: '#6b7280' }}>Things about you, in one place</div>}
            {t && t.key === 'cards' && <div style={{ fontSize: 12, color: '#6b7280' }}>License, OSHA, CPR, DOT physical… the office checks each one.</div>}
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: '#999', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}>
          {!t ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {TILES.map(x => (
                <button key={x.key} onClick={() => setTab(x.key)} style={{ textAlign: 'left', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '16px 14px', cursor: 'pointer', fontFamily: 'inherit', minHeight: 110, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 26 }}>{x.icon}</span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{x.label}</span>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{x.sub}</span>
                </button>
              ))}
            </div>
          ) : tab === 'general' ? <General />
            : tab === 'cards' ? <CertsPanel />
            : tab === 'updates' ? <Updates />
            : tab === 'hours' ? <Hours />
            : tab === 'timeoff' ? <TimeOff />
            : tab === 'safety' ? <Safety />
            : <AskCards waitingOnly={false} emptyText="Nothing from the office yet. When the office asks you something — a question, an RSVP, something to acknowledge — it shows up here with your answer." />}
        </div>
      </div>
    </div>, document.body);
}
export default MyInfoSheet;
